import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Banknote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Dialog to release payment against an approved OPA.
 *
 * Props:
 *  - opa: the OPA doc (with id, code, amount, purpose) | null
 *  - onClose: () => void
 *  - onReleased?: (updatedDoc) => void
 */
export default function ReleasePaymentDialog({ opa, onClose, onReleased }) {
  const [txnId, setTxnId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (!opa) return null;

  const reset = () => { setTxnId(""); setRemarks(""); setConfirming(false); };

  const handleClose = () => { reset(); onClose(); };

  const handleClick = () => {
    if (!txnId.trim()) {
      toast.error("Transaction ID is required");
      return;
    }
    if (!confirming) {
      setConfirming(true);
      return;
    }
    submit();
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/opa-requests/${opa.id}/release`, {
        transaction_id: txnId.trim(),
        transfer_date: date || null,
        remarks: remarks.trim() || null,
      });
      toast.success(`Payment released against ${opa.code} · moved to Payment Done`);
      onReleased && onReleased(data);
      reset();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Release failed");
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!opa} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-md"
        data-testid="release-payment-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Banknote className="w-5 h-5 text-emerald-400" /> Release Payment
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Recording bank transaction for{" "}
            <span className="font-mono text-emerald-400">{opa.code}</span> —{" "}
            <span className="font-mono text-white">
              {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(opa.amount || 0)}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs text-zinc-400">Transaction ID / UTR <span className="text-red-400">*</span></Label>
            <Input
              value={txnId}
              onChange={(e) => setTxnId(e.target.value)}
              placeholder="e.g., UTR2026061600012345"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="release-txn-id"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Transfer Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="release-date"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Remarks</Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional note about the disbursal"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white min-h-[80px]"
              data-testid="release-remarks"
            />
          </div>
        </div>
        <DialogFooter className="mt-4 flex-col gap-3 sm:flex-col sm:items-stretch">
          {confirming && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2" data-testid="release-confirm-banner">
              Are you sure? Click <span className="font-medium">Confirm Release</span> again to record the payment and move this OPA to <span className="font-medium">Payment Done</span>.
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => { if (confirming) setConfirming(false); else handleClose(); }}
              disabled={saving}
              className="text-zinc-400 hover:text-white"
              data-testid="release-cancel"
            >
              {confirming ? "Go back" : "Cancel"}
            </Button>
            <Button
              onClick={handleClick}
              disabled={saving || !txnId.trim()}
              className={confirming
                ? "bg-amber-500 text-black hover:bg-amber-400 font-medium"
                : "bg-emerald-500 text-black hover:bg-emerald-400 font-medium"}
              data-testid="release-submit"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {confirming ? "Confirm Release" : "Release Payment"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
